import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import MediaComponent from '../Media/media-component';
import { ASSETS } from '../../Public/assets-data';
import { getScreenColors, getScreenColorsUI } from '../../Public/screen-colors-data';

/**
 * ScreenColorsSection, an interactive mesh-color picker. A row of swatches on
 * one side; choosing one updates the large preview image. All swatches/copy are
 * data-driven from screen-colors-data. Mirrors for RTL. Used on the services
 * listing page and every service detail page.
 */
export default function ScreenColorsSection() {
    const { language = 'he', dir = 'rtl' } = useSelector((s) => s.systemData || {});
    const colors = getScreenColors();
    const ui = getScreenColorsUI(language);
    const [idx, setIdx] = useState(0);

    const [touched, setTouched] = useState(false);

    if (!colors.length) return null;
    const isHe = language === 'he';
    const nameOf = (c) => c.name?.[language === 'he' ? 'he' : 'en'] || c.name?.en || '';
    const active = colors[idx];

    // Smooth-scroll to the on-page contact form (added id="contact"), falling back
    // to the legacy #contact-form anchor. Keeps the visitor on the page.
    const goToContact = (e) => {
        const el = document.getElementById('contact') || document.getElementById('contact-form');
        if (el) {
            e.preventDefault();
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    return (
        <section dir={dir} className="w-full bg-white py-16 lg:py-24 px-6">
            <div className="mx-auto grid max-w-[1180px] grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">

                {/* Large preview of the selected color */}
                <div className="order-2 lg:order-1">
                    <div className="relative aspect-[4/3] w-full overflow-hidden rounded-3xl bg-primary-50 ring-1 ring-primary-100">
                        <MediaComponent
                            key={active.key}
                            src={active.image}
                            alt={nameOf(active)}
                            objectFit="cover"
                            quality={80}
                            useSekeleton={false}
                            wrapperClassName="absolute inset-0 h-full w-full"
                            className="h-full w-full"
                        />
                        <span className="absolute bottom-4 left-4 rounded-full bg-white/90 px-4 py-1.5 text-sm font-bold text-primary-500 shadow-sm backdrop-blur-sm">
                            {nameOf(active)}
                        </span>
                    </div>
                </div>

                {/* Picker */}
                <div className="order-1 lg:order-2">
                    {ui.eyebrow && (
                        <span className="text-xs font-bold uppercase tracking-[0.16em] text-secondary-500">{ui.eyebrow}</span>
                    )}
                    <h2 className="mt-2 font-secondary text-4xl font-extrabold leading-[0.95] tracking-[-0.02em] text-primary-500 md:text-5xl">
                        {ui.title}
                    </h2>
                    <p className="mt-4 max-w-md text-base text-primary-300 md:text-lg">{ui.subtitle}</p>

                    <div className="mt-8 grid grid-cols-3 place-items-center gap-x-4 gap-y-6 sm:flex sm:flex-wrap sm:place-items-start sm:gap-x-5 sm:gap-y-6">
                        {colors.map((c, i) => {
                            const selected = i === idx;
                            return (
                                <button
                                    key={c.key}
                                    type="button"
                                    onClick={() => { setIdx(i); setTouched(true); }}
                                    aria-pressed={selected}
                                    aria-label={nameOf(c)}
                                    title={nameOf(c)}
                                    className="group flex flex-col items-center gap-2"
                                >
                                    <span
                                        className={`h-14 w-14 overflow-hidden rounded-full ring-2 ring-offset-2 transition-all duration-200 ${selected ? 'scale-105 ring-secondary-500' : 'ring-primary-100 group-hover:ring-primary-300'}`}
                                        style={{ backgroundColor: c.hex }}
                                    >
                                        <MediaComponent
                                            src={c.image}
                                            alt={nameOf(c)}
                                            objectFit="cover"
                                            quality={40}
                                            useSekeleton={false}
                                            wrapperClassName="h-full w-full"
                                            className="h-full w-full"
                                        />
                                    </span>
                                    <span className={`text-xs font-semibold transition-colors ${selected ? 'text-primary-500' : 'text-primary-300'}`}>
                                        {nameOf(c)}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    {/* Gentle inline nudge, appears once a color is picked. Not a
                        modal, so it never interrupts browsing; just offers a quick
                        path to the contact form. */}
                    <AnimatePresence>
                        {touched && (
                            <motion.div
                                initial={{ opacity: 0, y: 16, scale: 0.96 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: 10, scale: 0.98 }}
                                transition={{ type: 'spring', stiffness: 420, damping: 24 }}
                                className="mt-8 flex flex-col gap-4 rounded-2xl bg-white p-4 shadow-[0_18px_45px_rgba(15,23,42,0.14)] ring-1 ring-primary-100 sm:flex-row sm:items-center sm:p-5"
                            >
                                <div className="flex items-start gap-3">
                                    <MediaComponent
                                        src={ASSETS.VERIFIED_VECTOR_ICON_ACCOUNT_VERIFICATION_VERIFICATION_ICON_564974_1246_BACKGROUND_REMOVED}
                                        alt=""
                                        width={80}
                                        objectFit="contain"
                                        useSekeleton={false}
                                        wrapperClassName="mt-0.5 h-8 w-8 shrink-0"
                                        className="h-full w-full"
                                    />
                                    <p className="text-sm text-primary-500 sm:text-base">
                                        <span className="font-bold">{isHe ? 'בחירה מצוינת!' : 'Great choice!'}</span>{' '}
                                        {isHe
                                            ? `${nameOf(active)}, רוצים להתקדם?`
                                            : `${nameOf(active)} it is, want to move forward?`}
                                    </p>
                                </div>
                                <a
                                    href="#contact"
                                    onClick={goToContact}
                                    className="inline-flex w-full shrink-0 items-center justify-center gap-2 rounded-full bg-secondary-500 px-6 py-3 text-sm font-bold text-white transition-all hover:brightness-105 active:scale-[0.98] sm:ms-auto sm:w-fit"
                                >
                                    {isHe ? 'צור קשר' : 'Move forward'}
                                    <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
                                </a>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </section>
    );
}
